import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  List,
  ListItem,
  ListIcon,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
} from "@chakra-ui/react";
import { 
  CheckCircleIcon, 
  WarningIcon,
  TimeIcon,
  LockIcon,
  EmailIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import axios from "axios";
import { normalizeEvaluationStatus } from "./evaluationStatus";

const WelcomePage = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [evaluationStatus, setEvaluationStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkEvaluationStatus = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/evaluate/${uuid}/status`
        );
        
        setEvaluationStatus(normalizeEvaluationStatus(response.data));
        setLoading(false);
      } catch (err) {
        console.error('Error checking evaluation status:', err);
        setError('Failed to check evaluation status. Please try again later.');
        setLoading(false);
      }
    };

    checkEvaluationStatus();
  }, [uuid]);

  useEffect(() => {
    // Redirect to last answered question when progress exists
    const savedQuestion = localStorage.getItem(`currentQuestion_${uuid}`);
    if (evaluationStatus?.canResume && savedQuestion) {
      navigate(`/evaluate/${uuid}/q/${savedQuestion}`);
    }
  }, [evaluationStatus, uuid, navigate]);

  const handleContinue = () => {
    // Pre-test pipeline: mic check → sample question → start (timer begins)
    navigate(`/evaluate/${uuid}/mic-check`);
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <VStack spacing={4}>
              <Spinner size="xl" color="purple.500" thickness="4px" />
              <Text color="gray.600">Checking evaluation status...</Text>
            </VStack>
          </Center>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                An error occurred
              </AlertTitle>
              <AlertDescription maxWidth="sm" mb={4}>
                {error}
              </AlertDescription>
              <Button colorScheme="red" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  // Handle case where evaluation doesn't exist (null/undefined status)
  if (evaluationStatus?.isMissing) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="warning"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Evaluation Not Found
              </AlertTitle>
              <AlertDescription maxWidth="sm" mb={4}>
                No evaluation exists with this ID.
              </AlertDescription>
              <Button 
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  // Handle completed evaluation
  if (evaluationStatus?.isCompleted) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="success"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="200px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Evaluation Already Completed
              </AlertTitle>
              <AlertDescription maxWidth="sm" mb={4}>
                You have already completed this evaluation.
              </AlertDescription>
              <Button 
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  if (evaluationStatus?.isSubmitted) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="container.md">
          <Center h="60vh">
            <Alert
              status="info"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="220px"
              borderRadius="lg"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                Evaluation Submitted
              </AlertTitle>
              <AlertDescription maxWidth="sm" mb={4}>
                Your responses have already been submitted and are currently being processed.
              </AlertDescription>
              <Button 
                bg="rgb(37, 87, 167)"
                color="white"
                _hover={{ bg: "rgb(27, 67, 137)" }}
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </Alert>
          </Center>
        </Container>
      </Box>
    );
  }

  // Default case - evaluation exists and is not completed
  return (
    <Box minH="100vh" bg="gray.50" py={4}>
      <Container maxW="container.lg">
        <Box bg="white" borderRadius="lg" borderWidth="1px" p={5} shadow="sm">
          <VStack spacing={3} align="stretch">
            <Heading size="lg" color="black" display="flex" alignItems="center" gap={2}>
              <CheckCircleIcon /> Évaluation des compétences
            </Heading>
            
            <VStack spacing={2} align="stretch">
              <Text fontSize="sm" color="gray.700" lineHeight="1.4">
                Bienvenue dans votre évaluation technique. Cette session comprend des questions à choix multiples et des questions ouvertes, conçues pour évaluer vos connaissances dans vos domaines d'expertise.
              </Text>
              
              <Heading size="sm" mt={1} display="flex" alignItems="center" gap={2}>
                ✏️ Instructions importantes :
              </Heading>
              
              <List spacing={0.5}>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={TimeIcon} color="blue.500" mt={0.5} />
                  <Text fontSize="sm" lineHeight="1.4">
                    Chaque question est chronométrée : assurez-vous d'avoir suffisamment de temps et un environnement calme.
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={CheckCircleIcon} color="green.500" mt={0.5} />
                  <Text fontSize="sm" lineHeight="1.4">
                    Pour les QCM, une ou plusieurs réponses peuvent être attendues.
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={InfoIcon} color="purple.500" mt={0.5} />
                  <Text fontSize="sm" lineHeight="1.4">
                    Pour les questions ouvertes, rédigez votre réponse dans l'espace dédié.
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={InfoIcon} color="blue.500" mt={0.5} />
                  <Text fontSize="sm" lineHeight="1.4">
                    Vous pouvez utiliser la fonctionnalité de dictée vocale pour répondre aux questions ouvertes.
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={Icon} viewBox="0 0 24 24" color="orange.500" mt={0.5}>
                    <path fill="currentColor" d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
                  </ListIcon>
                  <Text fontSize="sm" lineHeight="1.4">
                    Vous passerez automatiquement à la question suivante à la fin du temps imparti.
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={LockIcon} color="red.500" mt={0.5} />
                  <Text fontSize="sm" lineHeight="1.4">
                    Il n'est pas possible de revenir en arrière.
                  </Text>
                </ListItem>
              </List>
              
              <Heading size="sm" mt={1} display="flex" alignItems="center" gap={2}>
                ⛔ Règles anti-triche (obligatoires) :
              </Heading>
              
              <List spacing={0.5}>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={WarningIcon} color="red.500" mt={0.5} />
                  <Text fontSize="sm" lineHeight="1.4">
                    Il est interdit de changer d'onglet ou de page pendant l'évaluation.
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={WarningIcon} color="red.500" mt={0.5} />
                  <Text fontSize="sm" lineHeight="1.4">
                    Le copier-coller des questions est strictement interdit.
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start">
                  <ListIcon as={WarningIcon} color="red.500" mt={0.5} />
                  <Text fontSize="sm" lineHeight="1.4">
                    Toute tentative de capture, enregistrement ou diffusion du contenu est formellement prohibée.
                  </Text>
                </ListItem>
              </List>
              
              <Alert status="warning" borderRadius="md" mt={1} py={1.5}>
                <AlertIcon />
                <Box>
                  <Text fontSize="xs" lineHeight="1.3" mt="15px">
                    Notre plateforme détecte automatiquement les comportements non autorisés. Tout manquement sera signalé à votre recruteur, entraînera l'invalidation de votre test, et pourra compromettre votre présentation au client.
                  </Text>
                </Box>
              </Alert>
              
              <Heading size="sm" mt={1} display="flex" alignItems="center" gap={2}>
                <EmailIcon /> Après l'évaluation :
              </Heading>
              
              <Text fontSize="sm" color="gray.700" lineHeight="1.4">
                Un rapport détaillé vous sera envoyé par email. Il sera également transmis à votre employeur et, le cas échéant, au client final. Ce dernier pourra vous poser des questions complémentaires lors des entretiens.
              </Text>
              
              <Box bg="purple.50" p={2.5} borderRadius="md" mt={1}>
                <Text fontSize="sm" color="purple.800" fontWeight="medium" textAlign="center">
                  Bonne chance dans cette évaluation !
                  <br />
                  L'équipe ASAP CV
                </Text>
              </Box>
            </VStack>
            
            <Button 
              bg="rgb(37, 87, 167)"
              color="white"
              _hover={{ bg: "rgb(27, 67, 137)" }}
              _active={{ bg: "rgb(17, 47, 107)" }}
              size="lg" 
              onClick={handleContinue}
              mt={2}
            >
              Continuer
            </Button>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default WelcomePage; 
